// src/components/files/FileSortControls.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown, ButtonGroup } from 'react-bootstrap';

const FileSortControls = ({ 
  sortType, 
  sortMethod, 
  sortOrder, 
  setSortType, 
  setSortMethod, 
  setSortOrder 
}) => {
  // Get label for sort type button
  const getSortTypeLabel = () => {
    switch (sortType) {
      case 'folders':
        return 'Folders';
      case 'images':
        return 'Images';
      case 'documents':
        return 'Documents';
      case 'videos':
        return 'Videos';
      default:
        return 'All Files';
    }
  };
  
  // Get label for sort method button
  const getSortMethodLabel = () => {
    let methodText = '';
    switch (sortMethod) {
      case 'name':
        methodText = 'Name';
        break;
      case 'size':
        methodText = 'Size';
        break;
      case 'date':
        methodText = 'Date';
        break;
      default:
        methodText = 'Name';
    }
    
    return `${methodText} (${sortOrder === 'asc' ? 'A-Z' : 'Z-A'})`;
  };
  
  // Handle sort type selection
  const handleSortTypeSelect = (type) => {
    if (type !== sortType) {
      setSortType(type);
    }
  };
  
  // Handle sort method selection
  const handleSortMethodSelect = (method, order) => {
    if (method !== sortMethod || order !== sortOrder) {
      setSortMethod(method);
      setSortOrder(order);
    }
  };
  
  // Handle clear sort
  const handleClearSort = () => {
    setSortType('all');
    setSortMethod('name');
    setSortOrder('asc');
  };
  
  return (
    <ButtonGroup className="sort-controls">
      {/* Sort Type Dropdown */}
      <Dropdown className="sort-dropdown" as={ButtonGroup}>
        <Dropdown.Toggle 
          variant="outline-secondary" 
          id="sort-type-dropdown"
          className="sort-type-btn"
        >
          <span className="d-none d-md-inline">Type: </span>
          {getSortTypeLabel()}
        </Dropdown.Toggle>
        
        <Dropdown.Menu>
          <Dropdown.Item 
            active={sortType === 'all'} 
            onClick={() => handleSortTypeSelect('all')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortType === 'all' ? 'visible' : 'hidden' }}></i>
            All Files
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortType === 'folders'} 
            onClick={() => handleSortTypeSelect('folders')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortType === 'folders' ? 'visible' : 'hidden' }}></i>
            Folders
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortType === 'images'} 
            onClick={() => handleSortTypeSelect('images')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortType === 'images' ? 'visible' : 'hidden' }}></i>
            Images
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortType === 'documents'} 
            onClick={() => handleSortTypeSelect('documents')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortType === 'documents' ? 'visible' : 'hidden' }}></i>
            Documents
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortType === 'videos'} 
            onClick={() => handleSortTypeSelect('videos')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortType === 'videos' ? 'visible' : 'hidden' }}></i>
            Videos
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      
      {/* Sort Method Dropdown */}
      <Dropdown className="sort-dropdown" as={ButtonGroup}>
        <Dropdown.Toggle 
          variant="outline-secondary" 
          id="sort-method-dropdown"
          className="sort-method-btn"
        >
          <span className="d-none d-md-inline">Sort: </span>
          {getSortMethodLabel()}
        </Dropdown.Toggle>
        
        <Dropdown.Menu>
          <Dropdown.Item 
            active={sortMethod === 'name' && sortOrder === 'asc'} 
            onClick={() => handleSortMethodSelect('name', 'asc')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortMethod === 'name' && sortOrder === 'asc' ? 'visible' : 'hidden' }}></i>
            Name (A-Z)
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortMethod === 'name' && sortOrder === 'desc'} 
            onClick={() => handleSortMethodSelect('name', 'desc')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortMethod === 'name' && sortOrder === 'desc' ? 'visible' : 'hidden' }}></i>
            Name (Z-A)
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortMethod === 'size' && sortOrder === 'asc'} 
            onClick={() => handleSortMethodSelect('size', 'asc')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortMethod === 'size' && sortOrder === 'asc' ? 'visible' : 'hidden' }}></i>
            Size (Smallest)
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortMethod === 'size' && sortOrder === 'desc'} 
            onClick={() => handleSortMethodSelect('size', 'desc')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortMethod === 'size' && sortOrder === 'desc' ? 'visible' : 'hidden' }}></i>
            Size (Largest)
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortMethod === 'date' && sortOrder === 'asc'} 
            onClick={() => handleSortMethodSelect('date', 'asc')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortMethod === 'date' && sortOrder === 'asc' ? 'visible' : 'hidden' }}></i>
            Date (Oldest)
          </Dropdown.Item>
          <Dropdown.Item 
            active={sortMethod === 'date' && sortOrder === 'desc'} 
            onClick={() => handleSortMethodSelect('date', 'desc')}
          >
            <i className="fas fa-check mr-2" style={{ visibility: sortMethod === 'date' && sortOrder === 'desc' ? 'visible' : 'hidden' }}></i>
            Date (Newest)
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={handleClearSort}>
            <i className="fas fa-times mr-2"></i>
            Clear Sort
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </ButtonGroup>
  );
};

FileSortControls.propTypes = {
  sortType: PropTypes.string.isRequired,
  sortMethod: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  setSortType: PropTypes.func.isRequired,
  setSortMethod: PropTypes.func.isRequired,
  setSortOrder: PropTypes.func.isRequired
};

export default FileSortControls;